// Group a flat Meeting[] into parent/child/one-off rows for MeetingTable.
// Parents are recurring schedules; children are occurrences linked via
// parentId; one-offs are everything else. A child whose parent is
// missing from the input falls back to a one-off row so the user can
// still see and act on the orphan record (the meetings tab does not
// surface a separate orphan row variant — orphan recovery is a future
// concern).

import type { Meeting } from "../../../../types";

export interface ChildRow {
  kind: "child";
  meeting: Meeting;
  parent: Meeting;
}
export interface ParentRow {
  kind: "parent";
  meeting: Meeting;
  children: ChildRow[];
}
export interface OneOffRow {
  kind: "one-off";
  meeting: Meeting;
}
export type Row = ParentRow | ChildRow | OneOffRow;

export function groupMeetings(meetings: Meeting[]): Row[] {
  const parents = new Map<string, Meeting>();
  const children = new Map<string, Meeting[]>();
  const oneOffs: Meeting[] = [];

  for (const m of meetings) {
    if (m.recurrenceRule && !m.parentId) {
      parents.set(m.id, m);
      if (!children.has(m.id)) children.set(m.id, []);
    } else if (m.parentId) {
      const list = children.get(m.parentId) ?? [];
      list.push(m);
      children.set(m.parentId, list);
    } else {
      oneOffs.push(m);
    }
  }

  const rows: Row[] = [];

  for (const [parentId, parent] of parents) {
    const childMeetings = (children.get(parentId) ?? []).sort((a, b) =>
      a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
    );
    rows.push({
      kind: "parent",
      meeting: parent,
      children: childMeetings.map((m) => ({
        kind: "child",
        meeting: m,
        parent,
      })),
    });
  }

  // Orphan children: parent absent from this list → render as one-off.
  for (const [parentId, list] of children) {
    if (parents.has(parentId)) continue;
    for (const m of list) {
      oneOffs.push(m);
    }
  }

  for (const m of oneOffs) {
    rows.push({ kind: "one-off", meeting: m });
  }

  return rows;
}
